import React from "react";
import { CiSearch } from "react-icons/ci";

export type SearchInputSize = "sm" | "md" | "lg" | "default" | "form";
export type SearchInputVariant = "default" | "soft";

type SearchInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "size"
> & {
  value: string;
  onChange?: (value: string) => void;
  onDebouncedChange?: (value: string) => void;
  debounceMs?: number;
  size?: SearchInputSize;
  variant?: SearchInputVariant;
  containerClassName?: string;
  iconClassName?: string;
};

const sizeClasses: Record<SearchInputSize, string> = {
  sm: "h-9 rounded-[8px] border border-[#E1E1E1] bg-white pl-9 pr-3 text-sm text-black transition-colors duration-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
  md: "h-10 rounded-[8px] border border-[#E1E1E1] bg-white pl-10 pr-4 text-md text-black transition-colors duration-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
  lg: "h-[50px] rounded-[8px] border border-[#E1E1E1] bg-white pl-10 pr-4 text-lg leading-normal text-black transition-colors duration-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
  default: "h-10 rounded-[8px] border border-[#E1E1E1] bg-white pl-10 pr-4 text-md text-black transition-colors duration-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
  form: "h-[50px] rounded-[8px] border border-[#E1E1E1] bg-white pl-10 pr-4 text-lg leading-normal text-black transition-colors duration-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
};

const softClasses =
  "h-10 rounded-[8px] border border-transparent bg-search-bg pl-9 pr-3 text-sm text-dark-blue placeholder:text-search-muted focus:border-transparent focus:ring-0";

export default function SearchInput({
  value,
  onChange,
  onDebouncedChange,
  debounceMs = 300,
  size = "md",
  variant = "default",
  id,
  className = "",
  containerClassName = "",
  iconClassName = "",
  placeholder = "Search",
  "aria-label": ariaLabel,
  disabled,
  ...props
}: SearchInputProps) {
  const generatedId = React.useId();
  const inputId = id || generatedId;

  React.useEffect(() => {
    if (!onDebouncedChange) return;
    const timeout = window.setTimeout(() => {
      onDebouncedChange(value);
    }, debounceMs);
    return () => window.clearTimeout(timeout);
  }, [value, debounceMs, onDebouncedChange]);

  return (
    <div className={["relative", containerClassName].filter(Boolean).join(" ")}>
      <CiSearch
        className={[
          "pointer-events-none absolute left-3 top-1/2 z-[1] h-5 w-5 -translate-y-1/2",
          variant === "soft" ? "text-search-muted" : "text-dark-grey",
          iconClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        aria-hidden
      />
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel || (typeof placeholder === "string" ? placeholder : "Search")}
        disabled={disabled}
        className={[
          "w-full outline-none",
          variant === "soft"
            ? softClasses
            : ["placeholder:text-black/55", sizeClasses[size]].join(" "),
          disabled ? "cursor-not-allowed opacity-60" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...props}
      />
    </div>
  );
}
